import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { MdFolder } from 'react-icons/md';
import { FaFilePdf } from 'react-icons/fa';

import { BaseURL } from '../../config/apiUrl';
import { Get } from '../../Axios/AxiosFunctions';
import { Button } from '../../Component/Button/Button';
import { Checkbox } from '../../Component/Checkbox/Checkbox';
import Loader from '../../Component/Loader';
import NoData from '../../Component/NoData/NoData';
import ModalSkeleton from '../../modals/ModalSkeleton';
import classes from './ExtractionReview.module.css';

// Deal-room file picker for extraction runs. Same browse endpoints as
// ImportTaxReturnModal, but returns the SharePoint identifiers the
// extraction backend needs: { fileId, fileName, driveId }.
const SelectFilesModal = ({ show, setShow, listingId, onSelect }) => {
  const { access_token: accessToken, company_id: companyId } = useSelector(
    (state) => state.authReducer,
  );

  const [loading, setLoading] = useState(false);
  const [parents, setParents] = useState([]);
  const [items, setItems] = useState([]);
  // Keyed by SharePoint fileId rather than by the folder record shown in the current view:
  // the suggested set spans several folders, and a tick has to survive browsing elsewhere.
  const [chosen, setChosen] = useState({});
  const [preflight, setPreflight] = useState(null);
  const [trail, setTrail] = useState([]);

  const getChildren = async (ps) => {
    const parent = ps[ps.length - 1];
    setLoading(true);
    const res = await Get(
      BaseURL(`data-room?child=${parent}`),
      accessToken,
      companyId,
    );
    setLoading(false);
    if (res?.data?.data?.folders) {
      setParents(ps);
      setItems(res?.data?.data?.folders?.[0]?.children || []);
      setTrail((t) => t.slice(0, ps.length - 1));
    }
  };

  const getRoot = async () => {
    setLoading(true);
    const res = await Get(
      BaseURL(`data-room/tax-return-folder?listingId=${listingId}`),
      accessToken,
      companyId,
    );
    setLoading(false);
    if (res?.data?.data) {
      setParents([res?.data?.data?._id]);
      setItems(res?.data?.data?.children || []);
    }
  };

  const chosenList = Object.values(chosen);

  /** filename shapes the extractor handles badly; advisory only, never a block */
  const suspect = (name) =>
    (preflight?.files || []).find((f) => f.fileName === name)?.unsuitable || null;

  const toggleFile = (item, on) => {
    if (!item?.fileId) return;
    setChosen((c) => {
      const next = { ...c };
      if (on)
        next[item.fileId] = {
          fileId: item.fileId,
          fileName: item.fileName || item.name,
          driveId: item.driveId,
        };
      else delete next[item.fileId];
      return next;
    });
  };

  const handleSelect = () => onSelect(chosenList);

  // What the deal room actually holds, before anything is picked. Without this the analyst
  // opens a folder tree with no idea which years are present, which files are duplicates,
  // or that a folder named "Financials" is an empty placeholder.
  const getPreflight = async () => {
    const res = await Get(
      BaseURL(`extraction/preflight/${listingId}`),
      accessToken,
      companyId,
    );
    if (res?.data?.data) {
      setPreflight(res.data.data);
      // Pre-tick what a valuation actually needs, returns for the last three years plus
      // the current-year P&L, so the common case is a confirmation rather than a hunt
      // through folders. Everything stays removable.
      const seeded = {};
      for (const f of res.data.data.suggested || []) {
        if (f.fileId)
          seeded[f.fileId] = {
            fileId: f.fileId,
            fileName: f.fileName,
            driveId: f.driveId,
            unsuitable: f.unsuitable,
          };
      }
      setChosen(seeded);
    }
  };

  useEffect(() => {
    getRoot();
    getPreflight();
  }, []);



  return (
    <ModalSkeleton
      show={show}
      setShow={setShow}
      modalClass={classes.pickerModal}
      width={'600px'}
      header={'Select Financial Documents to Extract'}
      showCloseIcon={true}
    >
      {preflight && (
        <div className={classes.pfBox}>
          <div className={classes.pfLine}>
            <b>{preflight.files.length} document(s)</b> in this deal room ·{' '}
            tax returns {preflight.taxYears.join(', ') || 'none'} · P&amp;L{' '}
            {preflight.plYears.join(', ') || 'none'}
          </div>
          {preflight.suggested?.length > 0 && (
            <div className={classes.pfLine}>
              Suggested for a valuation:{' '}
              {preflight.suggested.map((s) => `${s.year} ${s.kind === 'tax_return' ? 'TR' : 'P&L'}`).join(' · ')}
            </div>
          )}
          {(preflight.warnings || []).map((w, i) => (
            <div className={classes.pfWarn} key={i}>{w}</div>
          ))}
        </div>
      )}
      <div className={classes.pfHelp}>
        Pick the <b>tax returns and P&amp;L statements</b> for the last three years plus the
        current year to date. Tick a file to include it; click a folder to open it. Financial
        documents are usually under <b>Broker</b> or a buyer's folder. Leave out valuations,
        CIMs, leases and offers, they are not financial statements.
      </div>
      {chosenList.length > 0 && (
        <div className={classes.chosenBox}>
          <div className={classes.chosenTitle}>
            Selected for extraction ({chosenList.length}), confirm or adjust
          </div>
          {chosenList.map((f) => (
            <div className={classes.chosenRow} key={f.fileId}>
              <span className={classes.chosenName} title={f.fileName}>
                {f.fileName}
                {f.unsuitable && (
                  <span className={classes.chosenWarn}>, {f.unsuitable}</span>
                )}
              </span>
              <span
                className={classes.chosenDrop}
                onClick={() => toggleFile({ fileId: f.fileId }, false)}
                title="remove from this run"
              >
                remove
              </span>
            </div>
          ))}
        </div>
      )}
      <div className={classes.pickerTrail}>
        {trail.length === 0 ? 'Top level' : trail.join('  ›  ')}
      </div>
      <div className={classes.pickerHeader}>
        <Button
          label={'Back'}
          disabled={parents?.length < 2}
          onClick={() => getChildren(parents.slice(0, parents.length - 1))}
        />
        <Button
          label={`Extract ${chosenList.length || ''} file(s)`}
          disabled={!chosenList.length}
          onClick={handleSelect}
        />
      </div>
      <div className={classes.pickerBody}>
        {loading ? (
          <Loader />
        ) : items?.length === 0 ? (
          <NoData text={'Empty folder'} />
        ) : (
          items.map((item) => (
            <div className={classes.pickerRow} key={item?._id}>
              {/* A file with no fileId cannot be fetched: extraction downloads through
                  SharePoint by fileId + driveId, and roughly 17,000 of the 23,400 file
                  records in the CRM carry neither. The checkbox used to accept the click
                  and silently drop it (toggleFile returns early on a missing fileId),
                  which reads as a broken picker rather than an unavailable file. */}
              <Checkbox
                label={' '}
                value={item?.fileId && chosen[item.fileId] ? ' ' : ''}
                setValue={(e, event) => {
                  event?.stopPropagation?.();
                  toggleFile(item, !!e?.includes(' '));
                }}
                disabled={!item?.isFile || !item?.fileId}
              />
              <div
                className={
                  item?.isFile && !item?.fileId
                    ? `${classes.pickerLabel} ${classes.pickerUnavailable}`
                    : classes.pickerLabel
                }
                title={
                  item?.isFile && !item?.fileId
                    ? 'This file has no SharePoint reference in the CRM, so it cannot be read. Re-upload it through the deal room to make it available.'
                    : undefined
                }
                onClick={() => {
                  if (item?.isFile) return;
                  setTrail((t) => [...t, item?.name]);
                  getChildren([...parents, item._id]);
                }}
                title={item?.isFile ? item?.fileName : item?.name}
              >
                {item?.isFile ? <FaFilePdf size={22} /> : <MdFolder size={22} />}
                <span>{item?.isFile ? item?.fileName : item?.name}</span>
                {item?.isFile && suspect(item.fileName || item.name) && (
                  <span className={classes.chosenWarn}>, {suspect(item.fileName || item.name)}
                  </span>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </ModalSkeleton>
  );
};

export default SelectFilesModal;
